import { useState } from "preact/hooks";
import { t } from "../lib/i18n.js";
import { API_BASE } from "../lib/api-base.js";

interface CsatRatingProps {
  tenantId: string;
  sessionId: string;
  conversationId: string;
  language: string;
  preview?: boolean;
}

export function CsatRating({ tenantId, sessionId, conversationId, language, preview }: CsatRatingProps) {
  const [submitted, setSubmitted] = useState<-1 | 1 | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!conversationId && !preview) return null;

  async function handleRate(rating: -1 | 1) {
    if (submitting || submitted !== null) return;
    setSubmitting(true);

    try {
      if (!preview) {
        await fetch(`${API_BASE}/api/chat/rate`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            conversation_id: conversationId,
            tenant_id: tenantId,
            session_id: sessionId,
            rating,
          }),
        });
      }
      setSubmitted(rating);
    } catch {
      // Silently fail — don't disrupt the chat experience
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted !== null) {
    return (
      <div class="flex items-center gap-1.5 text-[11px] text-[var(--muted-foreground)]">
        {submitted === 1 ? (
          <svg class="size-3 text-[var(--primary)]" viewBox="0 0 24 24" fill="currentColor" stroke="none">
            <path d="M7 10v12M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z" />
          </svg>
        ) : (
          <svg class="size-3 text-[var(--muted-foreground)]" viewBox="0 0 24 24" fill="currentColor" stroke="none">
            <path d="M17 14V2M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88Z" />
          </svg>
        )}
        <span>{t("csat.thanks", language)}</span>
      </div>
    );
  }

  return (
    <div class="flex items-center gap-1">
      <button
        onClick={() => handleRate(1)}
        disabled={submitting}
        class="size-6 rounded-md flex items-center justify-center hover:bg-[var(--background)] transition-colors disabled:opacity-50 focus-visible:outline-none"
        aria-label="Thumbs up"
      >
        <svg class="size-3.5 text-[var(--muted-foreground)]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M7 10v12" />
          <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z" />
        </svg>
      </button>
      <button
        onClick={() => handleRate(-1)}
        disabled={submitting}
        class="size-6 rounded-md flex items-center justify-center hover:bg-[var(--background)] transition-colors disabled:opacity-50 focus-visible:outline-none"
        aria-label="Thumbs down"
      >
        <svg class="size-3.5 text-[var(--muted-foreground)]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M17 14V2" />
          <path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88Z" />
        </svg>
      </button>
    </div>
  );
}
